import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EventModel, EventResponse, RSVPData, EventStatus } from '../types/Event';
import GradientBackground from '../components/GradientBackground';
import EventHeader from '../components/EventHeader';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { getEventById } from '../services/eventService';

const EventDetail: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { t } = useTranslation();
  const [event, setEvent] = useState<EventModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [rsvpData, setRsvpData] = useState<RSVPData[]>([]);
  const [showStoreModal, setShowStoreModal] = useState(false);

  // Auto-attempt to open the app when page loads
  // NOTE: Only do this if the page was opened in a browser,
  // NOT if it was accessed via Universal Link (which would have
  // already opened the app)
  useEffect(() => {
    const attemptAppOpen = () => {
      // Don't attempt if page was just loaded (likely from Universal Link)
      // Universal Links should handle app opening automatically
      // Only try custom scheme if user explicitly navigated to the URL in browser
      const isDirectBrowserAccess = 
        document.referrer && 
        (document.referrer.includes('google') || 
         document.referrer.includes('bing') || 
         document.referrer.includes('yahoo') ||
         document.referrer.includes('twitter') ||
         document.referrer.includes('facebook'));
      
      if (!isDirectBrowserAccess) {
        // If no referrer or not from social/search,
        // user likely typed URL or came from Universal Link
        // Universal Link already opened app, so don't interfere
        return;
      }
      
      // Only for users coming from search/social - try custom scheme
      const appUrl = `allezhopp://event/${eventId}`;
      
      // Create a hidden iframe to attempt the app launch
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = appUrl;
      document.body.appendChild(iframe);
      
      // Remove the iframe after a short delay
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
      }, 1000);
    };

    // Attempt to open app after a short delay
    const timer = setTimeout(attemptAppOpen, 1000);
    
    return () => clearTimeout(timer);
  }, [eventId]);

  // Fetch event from Firebase or show demo
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        
        // If no eventId or it's 'demo', show demo event
        if (!eventId || eventId === 'demo') {
          // Demo event data
          const demoEvent: EventModel = {
            id: 'demo-event-id',
            name: t('event.demo.name'),
            creatorUid: 'demo-creator',
            createdAt: new Date().toISOString(),
            eventDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
            invitedUids: ['user1', 'user2', 'user3'],
            votedIdeaIds: [],
            userVotes: {},
            description: t('event.demo.description'),
            votingDeadline: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days from now
            adminUids: ['admin1'],
            eventResponses: {
              'user1': 'yes',
              'user2': 'maybe',
              'user3': 'no',
            },
            status: EventStatus.active,
            backgroundIndex: 0,
            imageName: 'poster_pizza.jpg',
            location: {
              name: t('event.demo.locationName'),
              address: t('event.demo.locationAddress'),
              latitude: 40.7128,
              longitude: -74.0060,
            },
            isOpenEvent: true,
          };
          
          setEvent(demoEvent);
          return;
        }
        
        // Try to fetch real event from Firebase
        const eventData = await getEventById(eventId);
        
        if (eventData) {
          setEvent(eventData);
        } else {
          setError(t('event.notFound.title'));
        }
      } catch (err) {
        setError(t('event.notFound.message'));
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId, t]);

  const handleRSVP = (response: EventResponse, email: string) => {
    // Check if this is a demo event
    const isDemoEvent = !eventId || eventId === 'demo';
    
    if (isDemoEvent) {
      // For demo events, show that it's not real
      alert(t('event.alerts.demoEvent'));
    } else {
      // For real events, direct users to download the app
      alert(t('event.alerts.downloadApp'));
    }
    
    // RSVP attempt logged for debugging (removed for security)
  };

  if (loading) {
    return (
      <>
        <GradientBackground backgroundIndex={0} />
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          color: 'white',
          fontSize: '18px',
          position: 'relative',
          zIndex: 1,
        }}>
          {t('event.loading')}
        </div>
      </>
    );
  }

  if (error || !event) {
    return (
      <>
        <GradientBackground backgroundIndex={0} />
        <div style={{
          minHeight: '100vh',
          width: '100%',
          position: 'relative',
          zIndex: 1,
        }}>
          {/* Navigation */}
          <Navigation />
          
          <div style={{
            padding: '40px 20px',
            color: 'white',
            position: 'relative',
            minHeight: 'calc(100vh - 80px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {/* Event Not Found Card */}
            <div style={{
              width: '100%',
              maxWidth: '480px',
              backgroundColor: 'rgba(255, 255, 255, 0.12)',
              borderRadius: '32px',
              padding: '60px 40px',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4), 0 8px 32px rgba(0, 0, 0, 0.2)',
              position: 'relative',
              overflow: 'hidden',
              textAlign: 'center',
            }}>
              {/* Subtle gradient overlay */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                borderRadius: '32px',
                pointerEvents: 'none',
              }} />

              {/* Search Icon */}
              <div style={{
                fontSize: '48px',
                marginBottom: '20px',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
                position: 'relative',
                zIndex: 1,
              }}>
                🔍
              </div>

              {/* Event Not Found Title */}
              <h1 style={{
                fontSize: '24px',
                fontWeight: '700',
                margin: '0 0 16px 0',
                lineHeight: '1.2',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.4)',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                position: 'relative',
                zIndex: 1,
              }}>
                {t('event.notFound.title')}
              </h1>

              {/* Event Not Found Message */}
              <p style={{
                fontSize: '15px',
                lineHeight: '1.6',
                opacity: 0.9,
                margin: '0 0 32px 0',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                fontWeight: '400',
                position: 'relative',
                zIndex: 1,
              }}>
                {error || t('event.notFound.message')}
              </p>

              {/* Call to Action */}
              <div style={{
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                padding: '24px',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                position: 'relative',
                zIndex: 1,
              }}>
                <p style={{
                  color: 'white',
                  fontSize: '15px',
                  margin: '0 0 16px 0',
                  lineHeight: '1.6',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  fontWeight: '400',
                }}>
                  🎉 <strong style={{ fontWeight: '600' }}>{t('event.notFound.cta')}</strong><br />
                  {t('event.notFound.ctaText')}
                </p>
                <button
                  onClick={() => setShowStoreModal(true)}
                  style={{
                    padding: '14px 28px',
                    backgroundColor: '#4CAF50',
                    border: '1px solid #45a049',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#45a049';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(76, 175, 80, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#4CAF50';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(76, 175, 80, 0.3)';
                  }}
                >
                  {t('event.notFound.button')}
                </button>
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <Footer />
        </div>

        {/* Store Selection Modal */}
        {showStoreModal && (
          <div
            onClick={() => setShowStoreModal(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              backdropFilter: 'blur(4px)',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '20px',
                padding: '24px',
                maxWidth: '400px',
                width: '90%',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
            <h2 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: '600',
              color: '#1A1A2E',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            }}>
              {t('event.modal.title')}
            </h2>
            <p style={{
              margin: 0,
              fontSize: '13px',
              color: '#666',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            }}>
              {t('event.modal.description')}
            </p>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                alignItems: 'center',
              }}>
                <button
                  onClick={() => {
                    window.open(
                      'https://apps.apple.com/us/app/allezhopp-event-planning/id6752710469',
                      '_blank'
                    );
                    setShowStoreModal(false);
                  }}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: '12px',
                    color: '#1A1A2E',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    width: 'fit-content',
                    minWidth: '200px',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <svg 
                    width="18" 
                    height="18" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ flexShrink: 0 }}
                  >
                    <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 9.11 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" fill="currentColor"/>
                  </svg>
                  App Store
                </button>
                <button
                  onClick={() => {
                    window.open(
                      'https://play.google.com/store/apps/details?id=app.allezhopp.events&pcampaignid=web_share',
                      '_blank'
                    );
                    setShowStoreModal(false);
                  }}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: '12px',
                    color: '#1A1A2E',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    width: 'fit-content',
                    minWidth: '200px',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <svg 
                    width="18" 
                    height="18" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ flexShrink: 0 }}
                  >
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" fill="currentColor"/>
                  </svg>
                  Google Play
                </button>
              </div>
              <button
                onClick={() => setShowStoreModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px',
                  color: '#666',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  marginTop: '0',
                  width: 'fit-content',
                  alignSelf: 'center',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)';
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <GradientBackground backgroundIndex={event.backgroundIndex} />
      <div style={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        margin: 0,
        padding: 0,
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Navigation */}
        <Navigation />

        {/* Event Content */}
        <div style={{ flex: 1 }}>
          <EventHeader event={event} onRSVP={handleRSVP} eventId={eventId} />
        </div>

        {/* Footer */}
        <Footer />
      </div>

      {/* Store Selection Modal */}
      {showStoreModal && (
        <div
          onClick={() => setShowStoreModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <h2 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: '600',
              color: '#1A1A2E',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            }}>
              {t('event.modal.title')}
            </h2>
            <p style={{
              margin: 0,
              fontSize: '13px',
              color: '#666',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            }}>
              {t('event.modal.description')}
            </p>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignItems: 'center',
            }}>
              <button
                onClick={() => {
                  window.open(
                    'https://apps.apple.com/us/app/allezhopp-event-planning/id6752710469',
                    '_blank'
                  );
                  setShowStoreModal(false);
                }}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px',
                  color: '#1A1A2E',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: 'fit-content',
                  minWidth: '200px',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 9.11 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" fill="currentColor"/>
                </svg>
                {t('event.modal.appStore')}
              </button>
              <button
                onClick={() => {
                  window.open(
                    'https://play.google.com/store/apps/details?id=app.allezhopp.events&pcampaignid=web_share',
                    '_blank'
                  );
                  setShowStoreModal(false);
                }}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px',
                  color: '#1A1A2E',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: 'fit-content',
                  minWidth: '200px',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" fill="currentColor"/>
                </svg>
                {t('event.modal.googlePlay')}
              </button>
            </div>
            <button
              onClick={() => setShowStoreModal(false)}
              style={{
                backgroundColor: 'transparent',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                color: '#666',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                marginTop: '0',
                width: 'fit-content',
                alignSelf: 'center',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)';
              }}
            >
              {t('event.modal.cancel')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default EventDetail;
